import { call, put, takeLatest, all } from 'redux-saga/effects';
import {
  GET_AGENT_INFO,
  TRIGGER_FORWARD_LOGIN,
  TRIGGER_LOGIN,
  GET_ROLES_AFTER_ACTIVE_PROFILE,
  GET_PERSONAL_DETAIL_AND_BUSINESS_INFORMATION,
  GET_IS_HOUSEHOLD,
  AUTHENTICATION_FORGET_PASSWORD,
  SEND_OTP_FORGET_PASSWORD,
  VERIFY_OTP_FORGET_PASSWORD,
  RESET_PASSWORD,
} from './constants';
import {
  forwardLoginFail,
  forwardLoginSuccess,
  getAgentInfo,
  setActiveProfile,
  setAgentInfo,
  setTokenLogin,
  setRolesAfterActiveProfile,
  setAllProfilesRoles,
  setPersonalDetailInformation,
  setBusinessInformation,
  setIsHousehold,
  setForgetPasswordFactors,
  setOtpForgetPassword,
} from './actions';
import { hasValue } from '@helpers/utils/utils';
import {
  authService,
  getAgentInfoDataService,
  getRolesAfterActiveProfileService,
  getPersonalDetailAndBusinessInformationDataService,
  checkIsHousehold,
  authenticationForgetPasswordService,
  sendOtpForgetPasswordService,
  verifyOtpForgetPasswordService,
  resetPasswordService,
} from '@services/user';
import { GlobalModal } from '@components/molecules/PopupModal/GlobalModal.jsx';
import { ErrorLevelConfig } from '@core/configureStore/ErrorLevelConfig.js';
import { loadingRequest, loadingSuccess } from '@stores/global/actions';

export function* triggerLogin() {
  try {
    yield put(loadingRequest());
    const response = yield call(authService);
    yield put(loadingSuccess());

    if (hasValue(response)) {
      yield put(setActiveProfile(response));
      yield put(
        setAllProfilesRoles(
          (response.profiles || []).flatMap((p) => p.roles || []),
        ),
      );
    }
  } catch (err) {
    // TODO: handle err business
    console.error(err);
    yield put(loadingSuccess());
  }
}

export function* getAgentInfoSaga() {
  try {
    const response = yield call(getAgentInfoDataService);

    yield put(
      setAgentInfo({
        contacts: response?.clientContacts || {},
        info: response?.clientRef || {},
        agentCode: response?.agentCode || '',
        personalId: response?.personalId || '',
        agentTitle: response?.agentTitle || '',
        channel: response?.channelName,
        officeProvinceName: response?.officeProvinceName || '',
        segments: response?.segments || [],
        agentChannel: response?.channelName || '',
      }),
    );
  } catch (err) {
    console.error(err);
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      onOk: () => {},
    });
  }
}

export function* triggerForwardLogin({ payload }) {
  try {
    if (payload) yield put(setTokenLogin(payload));

    yield put(loadingRequest());
    const responseAuth = yield call(authService);
    yield put(loadingSuccess());

    if (hasValue(responseAuth)) {
      const allRoles = (responseAuth.profiles || []).flatMap(
        (p) => p.roles || [],
      );
      yield all([
        put(setActiveProfile(responseAuth)),
        put(getAgentInfo()),
        put(setAllProfilesRoles(allRoles)),
      ]);
    }

    yield put(forwardLoginSuccess());
  } catch (err) {
    console.error(err);
    yield put(loadingSuccess());
    yield put(forwardLoginFail());
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      onOk: () => {
        // Do nothing
      },
    });
  }
}

export function* getRolesAfterActiveProfileSaga() {
  try {
    const params = new URLSearchParams();
    params.append('data', '');
    const response = yield call(getRolesAfterActiveProfileService, params);
    yield put(setRolesAfterActiveProfile(response?.roles));
  } catch (err) {
    console.error(err);
    // GlobalModal.openOnce({
    //   title: 'Error',
    //   message: ErrorLevelConfig.SAGA.FETCH_ERROR,
    //   onOk: () => {},
    // });
  }
}

export function* getPersonalDetailAndBusinessInformationSaga() {
  try {
    const response = yield call(
      getPersonalDetailAndBusinessInformationDataService,
    );
    if (response?.responseCode === 'FOUND') {
      if (response?.responsePayload?.personalInfo) {
        yield put(
          setPersonalDetailInformation(response?.responsePayload?.personalInfo),
        );
      } else {
        yield put(setPersonalDetailInformation({}));
      }
      if (response?.responsePayload?.businessInfo) {
        yield put(
          setBusinessInformation(response?.responsePayload?.businessInfo),
        );
        if (Object.values(response?.responsePayload?.businessInfo).length) {
          yield put(setIsHousehold(true));
        }
      } else {
        yield put(setBusinessInformation({ taxCode: '' }));
      }
    }
  } catch (err) {
    console.error(err);
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      onOk: () => {},
    });
  }
}

export function* checkIsHouseholdSaga() {
  try {
    const response = yield call(checkIsHousehold);
    if (response) {
      yield put(setIsHousehold(true));
    } else {
      yield put(setIsHousehold(false));
    }
  } catch (err) {
    console.error(err);
    yield put(setIsHousehold(false));
  }
}

export function* authenticationForgetPasswordSaga({ payload, callback }) {
  let times = 1;
  yield put(loadingRequest());
  do {
    try {
      const response = yield call(authenticationForgetPasswordService, payload);
      if (callback) {
        times = 3;
        yield put(setForgetPasswordFactors(response));
        callback({ success: true, response });
      }
      yield put(loadingSuccess());
    } catch (err) {
      switch (err.response.status) {
        case 400: {
          times = 3;
          callback({ success: false, response: err.response.data });
          break;
        }
        default: {
          times++;
        }
      }
    }
  } while (times <= 2);
  yield put(loadingSuccess());
}

export function* sendOtpForgetPasswordSaga({ payload, callback }) {
  let times = 1;
  yield put(loadingRequest());
  do {
    yield put(loadingSuccess());
    try {
      yield put(loadingRequest());
      const response = yield call(sendOtpForgetPasswordService, payload);
      if (callback) {
        times = 3;
        yield put(setOtpForgetPassword(response));
        callback({ success: true, response });
      }
      yield put(loadingSuccess());
    } catch (err) {
      switch (err.response.status) {
        case 400: {
          times = 3;
          callback({ success: false, response: err.response.data });
          break;
        }
        default: {
          times++;
        }
      }
    }
  } while (times <= 2);
  yield put(loadingSuccess());
}

export function* verifyOtpForgetPasswordSaga({ payload, callback }) {
  let times = 1;
  yield put(loadingRequest());
  do {
    try {
      const response = yield call(verifyOtpForgetPasswordService, payload);
      if (callback) {
        times = 3;
        callback({ success: true, response });
      }
      yield put(loadingSuccess());
    } catch (err) {
      switch (err.response.status) {
        case 400: {
          times = 3;
          callback({ success: false, response: err.response.data });
          break;
        }
        default: {
          times++;
        }
      }
    }
  } while (times <= 2);
  yield put(loadingSuccess());
}

export function* resetPasswordSaga({ payload, callback }) {
  let times = 1;
  yield put(loadingRequest());
  do {
    try {
      const response = yield call(resetPasswordService, payload);
      if (callback) {
        times = 3;
        callback({ success: true, response });
      }
      yield put(loadingSuccess());
    } catch (err) {
      switch (err.response.status) {
        case 400: {
          times = 3;
          callback({ success: false, response: err.response.data });
          break;
        }
        default: {
          times++;
        }
      }
    }
  } while (times <= 2);
  yield put(loadingSuccess());
}

export default function* authSaga() {
  yield takeLatest(TRIGGER_LOGIN, triggerLogin);
  yield takeLatest(GET_AGENT_INFO, getAgentInfoSaga);
  yield takeLatest(TRIGGER_FORWARD_LOGIN, triggerForwardLogin);
  yield takeLatest(
    GET_ROLES_AFTER_ACTIVE_PROFILE,
    getRolesAfterActiveProfileSaga,
  );
  yield takeLatest(
    GET_PERSONAL_DETAIL_AND_BUSINESS_INFORMATION,
    getPersonalDetailAndBusinessInformationSaga,
  );
  yield takeLatest(GET_IS_HOUSEHOLD, checkIsHouseholdSaga);
  yield takeLatest(
    AUTHENTICATION_FORGET_PASSWORD,
    authenticationForgetPasswordSaga,
  );
  yield takeLatest(SEND_OTP_FORGET_PASSWORD, sendOtpForgetPasswordSaga);
  yield takeLatest(VERIFY_OTP_FORGET_PASSWORD, verifyOtpForgetPasswordSaga);
  yield takeLatest(RESET_PASSWORD, resetPasswordSaga);
}
